import React from 'react';
import { SparkIcon, Text, TooltipTrigger } from '@spark';
import AnalysisPropertyRow from '@/features/datasets/components/DataCollection/dataOverview/sections/AnalysisPropertyRow';
import '@/features/datasets/components/DataCollection/dataOverview/sections/InsightsSection.css';

const DEFAULT_INSIGHT_TEXT = 'Your data looks good';

/**
 * Outline status glyphs — Figma Data Overview uses Circle Alert Triangle for
 * warnings (elevated orange), check/x for good/error. Info-type insights have
 * no leading status icon. Explanations live on the status icon tooltip.
 */
const iconForInsightType = (type) => {
    switch (type) {
        case 'warning':
            return { slug: 'circle-alert-triangle', tone: 'warning' };
        case 'error':
            return { slug: 'circle-x', tone: 'error' };
        case 'good_insight':
            return { slug: 'circle-check', tone: 'success' };
        case 'info':
        default:
            return null;
    }
};

const InsightStatusIcon = ({ type, tooltip }) => {
    const icon = iconForInsightType(type);
    if (!icon) return null;
    const glyph = (
        <span
            className={`data-overview-insights-feedback data-overview-insights-feedback--${icon.tone}`}
            aria-hidden={tooltip ? undefined : true}
            tabIndex={tooltip ? 0 : undefined}
            aria-label={tooltip || undefined}
        >
            <SparkIcon slug={icon.slug} size={16} />
        </span>
    );
    if (!tooltip) return glyph;
    return (
        <TooltipTrigger text={tooltip} placement="top">
            {glyph}
        </TooltipTrigger>
    );
};

/**
 * Insights list ("Your data looks good" / warnings / errors). Shared between
 * the unified Data Overview card so the task landing surfaces
 * the exact same per-objective findings.
 *
 * @param {Array} props.insights Insights to render. May be empty/undefined.
 * @param {boolean} [props.showHeader=true] When false, omits the "Insights"
 *   heading row (used by the right-column instance so it sits flush under
 *   the labels panel without adding a duplicate heading).
 * @param {boolean} [props.showDefaultWhenEmpty=true] When false, renders
 *   nothing if the insights list is empty (used by the left-column instance
 *   so the "Your data looks good" fallback only shows on the right).
 */
const InsightsSection = ({ insights, showHeader = true, showDefaultWhenEmpty = true }) => {
    const hasInsights = !!(insights && insights.length > 0);
    if (!hasInsights && !showDefaultWhenEmpty) return null;

    const effectiveInsights = hasInsights
        ? insights
        : [{ type: 'info', content: DEFAULT_INSIGHT_TEXT }];

    const renderItem = (insight, index) => {
        const type =
            insight.content === DEFAULT_INSIGHT_TEXT ? 'good_insight' : insight.type;
        return (
            <div
                key={`${insight.content}-${index}`}
                className="data-overview-insights-item"
            >
                <div className="data-overview-insights-item__lead">
                    <InsightStatusIcon type={type} tooltip={insight.explanation} />
                    <Text
                        as="div"
                        variant="detail-regular"
                        color="secondary"
                        className="data-overview-insights-text"
                    >
                        {insight.content}
                    </Text>
                </div>
            </div>
        );
    };

    const list = (
        <div className="data-overview-insights-list">
            {effectiveInsights.map(renderItem)}
        </div>
    );

    if (!showHeader) {
        return <div className="data-overview-insights">{list}</div>;
    }

    // Figma: bulb + "Insights" (base-regular / primary), findings as detail-regular / secondary.
    return (
        <AnalysisPropertyRow icon="bulb" label="Insights">
            <div className="data-overview-insights data-overview-insights--flush">
                {effectiveInsights.map(renderItem)}
            </div>
        </AnalysisPropertyRow>
    );
};

export default InsightsSection;
